import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import * as Clipboard from "expo-clipboard";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { listAllCompanies, type AdminCompanyListRow } from "../../../../lib/admin";
import {
  createCompetition,
  getClientCompetition,
  updateCompetition,
} from "../../../../lib/clientCompetitions";
import { buildInviteUrl, createInvite } from "../../../../lib/invites";
import { supabase } from "../../../../lib/supabase";
import type { ClientCompetitionListRow } from "../../../../types/database";
import { COLORS, FONTS } from "../../../../lib/brand";
import { PANE_THEMES } from "../../../../components/brand/paneThemes";
import { AdminInput, AdminPageHeader } from "../../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

const BLACK = THEME.pageBg;
const FOREST_MID = THEME.surfaceRaisedBg;
const FOREST = THEME.sidebarBg;
const AMBER = THEME.accent;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const RED = THEME.destructive;
const BORDER = "rgba(255,255,255,0.07)";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export default function AdminNewRmProgramScreen() {
  const params = useLocalSearchParams<{
    companyId?: string | string[];
    competitionId?: string | string[];
  }>();
  const initialCompanyId = firstParam(params.companyId) ?? null;
  const initialCompetitionId = firstParam(params.competitionId) ?? null;

  const [companies, setCompanies] = useState<AdminCompanyListRow[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(initialCompanyId);
  const [companyQuery, setCompanyQuery] = useState("");
  const [name, setName] = useState("");
  const [brief, setBrief] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [competitionId, setCompetitionId] = useState<string | null>(initialCompetitionId);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [inviteExpires, setInviteExpires] = useState("");
  const [copied, setCopied] = useState(false);
  const [sentToast, setSentToast] = useState<string | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sentToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) {
      try {
        setCompanies(await listAllCompanies());
      } finally {
        hasLoadedRef.current = true;
      }
    }
    if (initialCompetitionId && !hydrated) {
      const row: ClientCompetitionListRow | null = await getClientCompetition(initialCompetitionId);
      if (row) {
        setName(row.name);
        setBrief(row.brief ?? "");
        setCompanyId(row.company_id);
        setCompetitionId(row.id);
      }
      setHydrated(true);
    } else if (!initialCompetitionId) {
      setHydrated(true);
    }
  }, [initialCompetitionId, hydrated]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  useEffect(() => {
    if (initialCompanyId && companies.find((c) => c.id === initialCompanyId)) {
      setCompanyId(initialCompanyId);
      const c = companies.find((cc) => cc.id === initialCompanyId);
      if (c) setCompanyQuery(c.name);
    }
  }, [initialCompanyId, companies]);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      if (sentToastTimerRef.current) clearTimeout(sentToastTimerRef.current);
    },
    []
  );

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyId) ?? null,
    [companies, companyId]
  );

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies.slice(0, 12);
    return companies.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 12);
  }, [companies, companyQuery]);

  async function onSaveDraft() {
    setErr(null);
    if (!companyId) return setErr("Pick a company first.");
    if (!name.trim()) return setErr("Give the program a name.");
    setBusy(true);
    if (competitionId) {
      const { error } = await updateCompetition(competitionId, {
        name: name.trim(),
        brief: brief.trim() || null,
      });
      setBusy(false);
      if (error) return setErr(error.message);
    } else {
      const { id, error } = await createCompetition({
        company_id: companyId,
        engagement_model: "rm",
        name: name.trim(),
        brief: brief.trim() || null,
        deliverable_description: null,
      });
      setBusy(false);
      if (error || !id) return setErr(error?.message ?? "Could not save the program.");
      setCompetitionId(id);
      router.setParams({ competitionId: id });
    }
  }

  async function onOpenFullEditor() {
    if (!competitionId) {
      await onSaveDraft();
      return;
    }
    router.push(`/client/competition/${competitionId}/edit` as Href);
  }

  async function onSendToClient() {
    setErr(null);
    setSentToast(null);
    if (!companyId) return setErr("Pick a company first.");
    if (!contactEmail.trim()) return setErr("Add a contact email so we know who to invite.");
    setBusy(true);

    // Always create the invite row first — it's the tracking record on the
    // /admin/invites page AND the magic-link source for the fallback modal
    // if the email send fails. Same pattern as `inviteManager` in
    // lib/managers.ts: DB row + Edge Function call run together.
    let row;
    try {
      row = await createInvite({
        kind: "client",
        email: contactEmail.trim(),
        companyId,
        role: "client",
      });
    } catch (e) {
      setBusy(false);
      return setErr(e instanceof Error ? e.message : "Could not create the invite.");
    }

    const url = buildInviteUrl(row.token);
    setInviteUrl(url);
    setInviteExpires(
      new Date(row.expires_at).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }),
    );

    const companyName =
      companies.find((c) => c.id === companyId)?.name ?? "your company";
    const trimmedEmail = contactEmail.trim();

    const { error: sendError } = await supabase.functions.invoke("invite-company-user", {
      body: {
        kind: "client",
        email: trimmedEmail,
        company_id: companyId,
        company_name: companyName,
        contact_name: "",
      },
    });

    setBusy(false);

    if (sendError) {
      setErr(
        `Couldn't send the invite email automatically (${sendError.message}). Copy the link below and send it manually.`,
      );
      setInviteOpen(true);
      return;
    }

    // Success — confirm via inline toast and clear the email field for the
    // next invite. The `invites` row stays in /admin/invites for tracking.
    setSentToast(
      `Invite sent to ${trimmedEmail}. They'll receive a link to access ${companyName} on Coravu.`,
    );
    if (sentToastTimerRef.current) clearTimeout(sentToastTimerRef.current);
    sentToastTimerRef.current = setTimeout(() => setSentToast(null), 6000);
    setContactEmail("");
  }

  return (
    <ScrollView style={{ flex: 1, backgroundColor: BLACK }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: 24, maxWidth: 880 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Programs · New RM"
          title={`Create RM program${selectedCompany ? ` for ${selectedCompany.name}` : ""}`}
          subtitle="Build the program with the client on the call. The full case editor opens in a separate tab — use it for deliverables, schedule, eligibility, judges. When you're ready, send the invite below."
        />

        {/* Not-yet-visible banner */}
        <View
          style={{
            marginTop: 14,
            paddingHorizontal: 14,
            paddingVertical: 12,
            borderRadius: 4,
            backgroundColor: "rgba(181,135,58,0.08)",
            borderWidth: 1,
            borderColor: "rgba(181,135,58,0.36)",
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Not visible to the client yet
          </Text>
          <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WS, lineHeight: 17 }}>
            This program is invisible to the client until you click &quot;Send to client&quot; below. They&apos;ll get an invite email and the program will be waiting in their portal when they sign in.
          </Text>
        </View>

        {err ? (
          <View
            style={{
              marginTop: 12,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderWidth: 1,
              borderColor: "rgba(220,80,80,0.40)",
              backgroundColor: "rgba(220,80,80,0.08)",
              borderRadius: 4,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: RED }}>{err}</Text>
          </View>
        ) : null}

        {sentToast ? (
          <View
            style={{
              marginTop: 12,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderWidth: 1,
              borderColor: "rgba(143,187,138,0.40)",
              backgroundColor: "rgba(143,187,138,0.08)",
              borderRadius: 4,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: COLORS.SAGE.hex }}>
              {sentToast}
            </Text>
          </View>
        ) : null}

        {/* Company picker */}
        <SectionLabel>Company</SectionLabel>
        {selectedCompany ? (
          <View
            style={{
              marginTop: 8,
              flexDirection: "row",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <View
              style={{
                flex: 1,
                minWidth: 200,
                padding: 14,
                borderRadius: 6,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: BORDER,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 13, color: LINEN }}>
                {selectedCompany.name}
              </Text>
              <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
                {selectedCompany.size ?? "—"} · {selectedCompany.industry ?? "—"}
              </Text>
            </View>
            <Pressable
              onPress={() => {
                setCompanyId(null);
                setCompanyQuery("");
              }}
              style={[
                {
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 3,
                  borderWidth: 1,
                  borderColor: BORDER,
                  backgroundColor: "rgba(255,255,255,0.02)",
                } as ViewStyle,
                Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
              ]}
            >
              <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: WS }}>Change</Text>
            </Pressable>
          </View>
        ) : (
          <View style={{ marginTop: 8 }}>
            <TextInput
              value={companyQuery}
              onChangeText={setCompanyQuery}
              placeholder="Search by name…"
              placeholderTextColor={WL}
              autoCapitalize="words"
              style={
                {
                  backgroundColor: FOREST_MID,
                  borderWidth: 1,
                  borderColor: BORDER,
                  borderRadius: 4,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  color: W,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : {}),
                } as unknown as TextStyle
              }
            />
            <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
              {filteredCompanies.length === 0 ? (
                <Text style={{ fontFamily: FONTS.body, fontSize: 11.5, color: WL }}>
                  No companies match. Use “+ New company” on the Companies page first.
                </Text>
              ) : (
                filteredCompanies.map((c) => (
                  <Pressable
                    key={c.id}
                    onPress={() => {
                      setCompanyId(c.id);
                      setCompanyQuery(c.name);
                    }}
                    style={[
                      {
                        paddingHorizontal: 10,
                        paddingVertical: 6,
                        borderRadius: 999,
                        borderWidth: 1,
                        borderColor: BORDER,
                        backgroundColor: "rgba(255,255,255,0.02)",
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: WS }}>
                      {c.name}
                    </Text>
                  </Pressable>
                ))
              )}
            </View>
          </View>
        )}

        {/* Program basics */}
        <SectionLabel>Program basics</SectionLabel>
        <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 11.5, color: WM }}>
          Capture what you heard on the call. You can refine in the full editor.
        </Text>

        <FieldLabel>Program name *</FieldLabel>
        <DarkInput value={name} onChange={setName} placeholder="e.g. Q3 Product Strategy Cohort" />

        <FieldLabel>Brief</FieldLabel>
        <DarkInput
          value={brief}
          onChange={setBrief}
          placeholder="A one-paragraph description of the problem you'll put in front of students."
          multiline
        />

        <FieldLabel>Contact email (for invite)</FieldLabel>
        <DarkInput
          value={contactEmail}
          onChange={setContactEmail}
          placeholder="contact@company.com"
          autoCapitalize="none"
          keyboardType="email-address"
        />

        {/* Action row */}
        <View style={{ marginTop: 18, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          <Pressable
            onPress={() => void onSaveDraft()}
            disabled={busy}
            style={[
              {
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 3,
                backgroundColor: AMBER,
                opacity: busy ? 0.5 : 1,
              } as ViewStyle,
              Platform.OS === "web" && !busy ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            {busy ? (
              <ActivityIndicator size="small" color={FOREST} />
            ) : (
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 11.5,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: FOREST,
                }}
              >
                {competitionId ? "Save draft" : "Save & start"}
              </Text>
            )}
          </Pressable>
          <Pressable
            onPress={() => void onOpenFullEditor()}
            disabled={busy || !competitionId}
            style={[
              {
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: AMBER,
                backgroundColor: "rgba(181,135,58,0.10)",
                opacity: busy || !competitionId ? 0.5 : 1,
              } as ViewStyle,
              Platform.OS === "web" && !busy && competitionId ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: AMBER,
              }}
            >
              Open full editor →
            </Text>
          </Pressable>
          <Pressable
            onPress={() => void onSendToClient()}
            disabled={busy || !competitionId || !contactEmail.trim()}
            style={[
              {
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 3,
                borderWidth: 1,
                borderColor: BORDER,
                backgroundColor: "rgba(255,255,255,0.02)",
                opacity: busy || !competitionId || !contactEmail.trim() ? 0.5 : 1,
              } as ViewStyle,
              Platform.OS === "web" && !busy && competitionId && contactEmail.trim() ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: WS,
              }}
            >
              Send to client →
            </Text>
          </Pressable>
        </View>

        {competitionId ? (
          <Text style={{ marginTop: 8, fontFamily: FONTS.body, fontSize: 11, color: WL }}>
            Draft saved. The full editor is the source of truth for everything else (deliverables, schedule, eligibility, judges).
          </Text>
        ) : null}
      </View>

      {/* Invite link modal */}
      <Modal visible={inviteOpen} transparent animationType="fade" onRequestClose={() => setInviteOpen(false)}>
        <View style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.65)", justifyContent: "center", paddingHorizontal: 16 }}>
          <View
            style={{
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
              backgroundColor: FOREST_MID,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: BORDER,
              padding: 22,
            }}
          >
            <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 22, color: LINEN }}>
              Email send failed
            </Text>
            <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: WM }}>
              The invite was created but the email didn't go out. Copy the link below and send it
              to the client manually.
            </Text>
            <Text style={{ marginTop: 10, fontFamily: FONTS.body, fontSize: 11, color: WM }}>
              Expires {inviteExpires}
            </Text>
            <Text
              selectable
              style={{
                marginTop: 8,
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderRadius: 4,
                backgroundColor: BLACK,
                borderWidth: 1,
                borderColor: BORDER,
                fontFamily: Platform.OS === "web" ? ("'JetBrains Mono', Menlo, monospace" as string) : "Menlo",
                fontSize: 11.5,
                color: LINEN,
                lineHeight: 17,
              }}
            >
              {inviteUrl}
            </Text>
            <View style={{ marginTop: 14, flexDirection: "row", gap: 10 }}>
              <Pressable
                onPress={() => {
                  void Clipboard.setStringAsync(inviteUrl);
                  setCopied(true);
                  if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
                  copiedTimerRef.current = setTimeout(() => setCopied(false), 1600);
                }}
                style={[
                  {
                    flex: 1,
                    paddingVertical: 10,
                    borderRadius: 3,
                    backgroundColor: AMBER,
                    alignItems: "center",
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11.5,
                    letterSpacing: 1.4,
                    textTransform: "uppercase",
                    color: FOREST,
                  }}
                >
                  {copied ? "Copied" : "Copy link"}
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setInviteOpen(false)}
                style={[
                  {
                    paddingHorizontal: 18,
                    paddingVertical: 10,
                    borderRadius: 3,
                    borderWidth: 1,
                    borderColor: BORDER,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12, color: WS }}>Close</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        marginTop: 22,
        fontFamily: FONTS.bodyBold,
        fontSize: 9.5,
        letterSpacing: 2,
        textTransform: "uppercase",
        color: AMBER,
      }}
    >
      {children}
    </Text>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        marginTop: 12,
        fontFamily: FONTS.bodyBold,
        fontSize: 9.5,
        letterSpacing: 1.6,
        textTransform: "uppercase",
        color: WM,
      }}
    >
      {children}
    </Text>
  );
}

function DarkInput({
  value,
  onChange,
  placeholder,
  multiline,
  autoCapitalize,
  keyboardType,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
}) {
  return (
    <AdminInput
      theme={THEME}
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      multiline={multiline}
      autoCapitalize={autoCapitalize}
      keyboardType={keyboardType === "phone-pad" ? "numeric" : keyboardType}
      minHeight={multiline ? 90 : undefined}
      style={{ marginTop: 6 }}
    />
  );
}
